import React, { useRef, useEffect, useState } from 'react';
import './pages-css/RichTextEditorStyles.css';

const baseURL = process.env.REACT_APP_API_BASE_URL || ''; // Adjust as needed

const RichTextEditor = ({ value, onChange, selectionRangeRef, setActiveTextArea, readOnly = false  }) => {
  const contentRef = useRef(null);

  // Recorder state
  const [showRecorder, setShowRecorder] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [recorderPos, setRecorderPos] = useState({ x: 0, y: 0 });

  // Save selection for insertion
  const savedSelectionRef = useRef(null);

  useEffect(() => {
    if (contentRef.current && value !== contentRef.current.innerHTML) {
      contentRef.current.innerHTML = value || '';
    }
  }, [value]);

  const handleFormat = (command) => {
    document.execCommand(command, false, null);
    if (contentRef.current) {
      onChange(contentRef.current.innerHTML);
    }
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    // Save the current selection
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      savedSelectionRef.current = selection.getRangeAt(0).cloneRange();
    }
    // NEW: Save mouse position for modal
    setRecorderPos({ x: e.clientX, y: e.clientY });
    setShowRecorder(true);
    startRecording();
  };

  const handleToolbarClick = (e) => {
    // Prevents the event from bubbling up to the form submit
    e.preventDefault();
    e.stopPropagation();
  };

  const handleInsertDx = (e) => {
    handleToolbarClick(e);
    // Restore selection if needed
    if (selectionRangeRef && selectionRangeRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(selectionRangeRef.current);
    }
    document.execCommand('insertHTML', false, '<li><b>Diagnosis </b>:</li>');
    if (contentRef.current) {
      onChange(contentRef.current.innerHTML);
    }
  };

  const handleInsertDxNumbered = (e) => {
    handleToolbarClick(e);
    if (selectionRangeRef && selectionRangeRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(selectionRangeRef.current);
    }
    document.execCommand('insertHTML', false, '<ol><li><b>Diagnosis </b>:</li></ol>');
    if (contentRef.current) {
      onChange(contentRef.current.innerHTML);
    }
  };

  const handleInsertDxTemplate = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // For two blank lines before insertion:
    const template = `\n\n<li><b>Diagnosis</b>\n     - \n     - \n     Plan: </li>`;
    
    document.execCommand('insertHTML', false, template);
    if (contentRef.current) {
      onChange(contentRef.current.innerHTML);
    }
  };

  // Start recording
  const startRecording = async () => {
    setShowRecorder(true);
    setIsRecording(true);
    setIsProcessing(false);
    setAudioChunks([]);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new window.MediaRecorder(stream);
      setMediaRecorder(recorder);
      const chunks = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };
      recorder.onstop = () => {
        setAudioChunks(chunks);
        handleTranscription(chunks);
        stream.getTracks().forEach(track => track.stop());
      };
      recorder.start();
    } catch (err) {
      alert('Microphone access denied or not available.');
      setShowRecorder(false);
      setIsRecording(false);
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      setIsRecording(false);
      mediaRecorder.stop();
    }
  };

  // Send audio to API and insert transcript
  const handleTranscription = async (chunks) => {
    setIsProcessing(true);
    const audioBlob = new Blob(chunks, { type: 'audio/webm' });
    const formData = new FormData();
    formData.append('file', audioBlob, 'audio.webm');

    try {
      const oauthToken = localStorage.getItem('AUTH_TOKEN');
      if (!oauthToken) {
        throw new Error('OAuth token is missing');
      }

      const response = await fetch(`${baseURL}/api/transcribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${oauthToken}`,
        },
        body: formData,
      });
      const data = await response.json();
      const transcript = data.transcription || data.transcript || '';
      insertTranscript(transcript);
    } catch (err) {
      alert('Transcription failed.');
    }
    setIsProcessing(false);
    setShowRecorder(false);
  };

  // Insert or replace text at saved selection
  const insertTranscript = (text) => {
    if (!contentRef.current) return;
    contentRef.current.focus();
    const selection = window.getSelection();
    selection.removeAllRanges();
    if (savedSelectionRef.current) {
      selection.addRange(savedSelectionRef.current);
    }
    // Replace selected text or insert at cursor
    document.execCommand('insertText', false, text);
    onChange(contentRef.current.innerHTML);
  };

  const handleKeyDown = (e) => {
    // Handle Tab key
    if (e.key === 'Tab') {
      e.preventDefault();
      document.execCommand('insertHTML', false, '\u00A0\u00A0\u00A0\u00A0\u00A0');
      if (contentRef.current) {
        onChange(contentRef.current.innerHTML);
      }
    }
    
    // Handle Ctrl+D for Dx Template
    if (e.ctrlKey && e.key === 'd') {
      e.preventDefault(); // Prevent default browser behavior
      handleInsertDxTemplate(e);
    }
  };

  return (
    <div className="rich-text-editor ml-[7px] mt-[15px]">
      <div className="editor-toolbar">
        <button className="toolbar-button" onClick={(e) => { handleToolbarClick(e); handleFormat('bold'); }}><b>B</b></button>
        <button className="toolbar-button" onClick={(e) => { handleToolbarClick(e); handleFormat('italic'); }}><i>I</i></button>
        <button className="toolbar-button" onClick={(e) => { handleToolbarClick(e); handleFormat('underline'); }}><u>U</u></button>
        <button className="toolbar-button" onClick={(e) => { handleToolbarClick(e); handleFormat('insertUnorderedList'); }}>• List</button>
        <button className="toolbar-button" onClick={(e) => { handleToolbarClick(e); handleFormat('insertOrderedList'); }}>1. List</button>
        <button className="toolbar-button" onClick={handleInsertDx} title="Insert Diagnosis (Bullet)">
          • Dx
        </button>
        <button className="toolbar-button" onClick={handleInsertDxNumbered} title="Insert Diagnosis (Numbered)">
          1. Dx
        </button>
        <button 
          className="toolbar-button" 
          onClick={handleInsertDxTemplate}
          title="Insert Diagnosis Template"
        >
          •Dx - Plan
        </button>
      </div>
      <div
        ref={contentRef}
        contentEditable={!readOnly}
        suppressContentEditableWarning
        className="editable-content"
        onInput={(e) => !readOnly && onChange(e.currentTarget.innerHTML)}
        onContextMenu={(e) => !readOnly && handleContextMenu(e)}
        onFocus={() => !readOnly && setActiveTextArea('RichTextEditor')}
        onKeyDown={(e) => !readOnly && handleKeyDown(e)}
        style={{
          border: '1px solid #ccc',
          padding: '10px',
          minHeight: '200px',
          whiteSpace: 'pre-wrap',
          borderRadius: '4px',
          backgroundColor: readOnly ? '#f5f5f5' : '#fff',
          cursor: readOnly ? 'not-allowed' : 'text',
        }}
      />
      {showRecorder && (
        <div
          className="recorder-modal"
          style={{
            position: 'fixed',
            left: recorderPos.x,
            top: Math.max(0, recorderPos.y - 70), // 70px above cursor, adjust as needed
            zIndex: 9999,
            background: '#fff',
            border: '2px solid #007bff',
            borderRadius: '8px',
            padding: '24px 32px',
            boxShadow: '0 4px 24px rgba(0,0,0,0.15)',
            textAlign: 'center',
          }}
        >
          {isRecording && (
            <>
              <div>Recording... <span className="recorder-dot" /></div>
              <button className="toolbar-button" onClick={stopRecording}>Stop</button>
            </>
          )}
          {isProcessing && (
            <div>
              <span className="spinner" /> Processing...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RichTextEditor;
